import {
  ContentLayout,
  ContentHeader,
  ContentHeaderTitle,
  ContentHeaderBreadcrumbs,
  ContentHeaderBackNavigation,
} from '@excent-one/react/layout/content';
import {
  ControlledComboBox,
  ControlledInput,
  FormLayout,
  FormSection,
  SearchFormLayout,
} from '@excent-one/react';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormFieldSet } from '@progress/kendo-react-form';
import { MonthView, Scheduler, SchedulerItem, SchedulerItemProps } from '@progress/kendo-react-scheduler';
import {
  getCoordinators,
  getMyDetails,
  getMyMembers,
  searchMyCalendar
} from '@phrmpc/infrastructure/phrmpc';
import { useParams, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { FilterLogic, FilterOperator, MONTHS_OPTIONS, YEARS_OPTIONS } from "@phrmpc/shared";
import { useAuthContext } from '@excent-one/auth';
import FormActionsBar from '@excent-one/react/form/layouts/FormActionsBar';
import { Button } from '@progress/kendo-react-all';
import { cancelIcon } from '@progress/kendo-svg-icons';

interface CalendarData {
  id: number;
  title: string | undefined;
  description: string | undefined;
  start: Date | null;
  end: Date | null;
}

interface SearchFormValues {
  monthId: number | null;
  year: number | null;
  coordinatorId?: string;
  coordinatorName?: string;
}

const formatTime = (value: string | Date | null | undefined): string => {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const defaultSearchValues: SearchFormValues = {
  monthId: new Date().getMonth() + 1,
  year: new Date().getFullYear(),
  coordinatorId: '',
  coordinatorName: '',
};

const buildFilter = (values: SearchFormValues, coordinatorOptions: any[] = []) => {
  const filters: { field: string; operator: number; value: unknown }[] = [];

  if (values.monthId) {
    filters.push({
      field: "monthId",
      operator: FilterOperator.Equals,
      value: values.monthId,
    });
  }
  if (values.year) {
    filters.push({
      field: "year",
      operator: FilterOperator.Equals,
      value: values.year,
    });
  }
  if (values.coordinatorId) {
    filters.push({
      field: "coordinatorId",
      operator: FilterOperator.Equals,
      value: values.coordinatorId,
    });
  } else { // this means User is AH and clear was triggered
    const coordinatorIds = coordinatorOptions.map(x => x.id);
    if (coordinatorIds.length > 0) {
      filters.push({
        field: "coordinatorId",
        operator: FilterOperator.Equals, // Fix IN operation
        value: coordinatorIds[0],
      });
    }
  }
  if (filters.length === 0) return null;
  return { logic: FilterLogic.And, filters } as const;
};


const MyCalendar = () => {
  const { month, year } = useParams({ strict: false });
  const router = useRouter();
  const [{ currentUser }, { isInAnyRole }] = useAuthContext();

  const isUserAreaHead = isInAnyRole('Area Head');
  const isUserManager = isInAnyRole('Manager');

  const { control, getValues, reset } = useForm<SearchFormValues>({
    defaultValues: defaultSearchValues,
  });

  const [calendarData, setCalendarData] = useState<CalendarData[]>([]);
  const [calendarMonth, setCalendarMonth] = useState<Date>();

  const { data: myDetails} = useQuery({
    queryKey: ["my-details", currentUser?.id],
    queryFn: () => getMyDetails(),
  });

  const { data: memberCoordinators } = useQuery({
    queryKey: ["my-members", currentUser?.id],
    queryFn: () => getMyMembers()
  });

  const { data: allCoordinatorsAndAreaHeads } = useQuery({
    queryKey: ["all-coordinators", currentUser?.id],
    queryFn: () => getCoordinators({
        payload: {
          pageSize: 10000,
        },
      } as any)
  });

  const coordinatorOptions = useMemo(() => {
    const members = isUserManager ? allCoordinatorsAndAreaHeads?.items ?? [] : (isUserAreaHead ? memberCoordinators?.items ?? [] : []);
    if (members.length === 0) {
      return [];
    }

    if (!myDetails) {
      return members;
    }
    return [
      {
        id: myDetails.id,
        fieldName: myDetails.fieldName,
      },
      ...members,
    ];
  }, [memberCoordinators?.items, allCoordinatorsAndAreaHeads?.items, myDetails]);

  const [appliedFilters, setAppliedFilters] =
    useState<SearchFormValues>(defaultSearchValues);
  const [skip, setSkip] = useState(0);

  const { data: pagedResult, refetch } = useQuery({
    queryKey: ["my-calendars", appliedFilters, skip],
    queryFn: () => {
      return searchMyCalendar({
        payload: {
          pageNumber: 1,
          pageSize: 1000, // MAX?
          filter: buildFilter(appliedFilters, coordinatorOptions) as any,
        },
      } as any);
    },
  });

  const handleSearch = () => {
    setSkip(0);
    setAppliedFilters(getValues());
    refetch();
  };

  const handleClear = () => {
    const coordinatorId = coordinatorOptions.length > 0 ? '' : (myDetails?.id ?? '');
    const coordinatorName = coordinatorOptions.length > 0 ? '' : (myDetails?.fieldName ?? '');
    const clearSearchValues: SearchFormValues = {
      monthId: null,
      year: null,
      coordinatorId,
      coordinatorName,
    };

    reset(clearSearchValues);
    setCalendarData([]);
  };

  const back = useCallback(() => {
    router.history.back();
  }, [router]);

  useEffect(() => {
    if (!myDetails) {
      return;
    }

    const today = new Date();
    const monthParam = month ? parseInt(month) : today.getMonth() + 1;
    const yearParam = year ? parseInt(year) : today.getFullYear();

    const coordinatorId = isUserAreaHead
      ? coordinatorOptions?.[0]?.id ?? ""
      : myDetails?.id ?? "";

    const coordinatorName = isUserAreaHead
      ? coordinatorOptions?.[0]?.fieldName ?? ""
      : myDetails?.fieldName ?? "";

    reset({
      monthId: today.getMonth() + 1,
      year: today.getFullYear(),
      coordinatorId,
      coordinatorName,
    });

    setSkip(0);
    setAppliedFilters({
      monthId: monthParam,
      year: yearParam,
      coordinatorId,
      coordinatorName,
    });
  }, [myDetails, coordinatorOptions, month, year, reset]);

  useEffect(() => {
    const searchResults = (pagedResult?.items ?? []).flatMap(
      row => row.myCalendarAssignments ?? []
    );

    const currentYear = new Date().getFullYear();
    const parseAdjust = (eventDate: Date | undefined, time: Date | undefined) => {
      if (eventDate === undefined) return null;

      const date = new Date(eventDate);
      date.setFullYear(currentYear);

      if (time) date.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);

      return date;
    };

    let id = 1;
    const mappedAssignments: CalendarData[] = (searchResults ?? []).map(
      ({ date, branchName, fromSchedule, toSchedule }) => ({
        id: id++,
        title: `${formatTime(fromSchedule)} - ${formatTime(toSchedule)}\n${(branchName ?? "").slice(0, 20)}${(branchName?.length ?? 0) > 20 ? "..." : ""}`,
        description: branchName,
        start: parseAdjust(date, fromSchedule),
        end: parseAdjust(date, toSchedule),
      }),
    );
    setCalendarData(mappedAssignments.sort((a, b) => a.start!.getTime() - b.start!.getTime()));

    const today = new Date();
    const currMonthYear =
      appliedFilters?.year && appliedFilters?.monthId
        ? new Date(appliedFilters.year, appliedFilters.monthId - 1, 1)
        : new Date(today.getFullYear(), today.getMonth(), 1);
    setCalendarMonth(currMonthYear);
  }, [pagedResult]);

  const gridData = useMemo(
    () =>
      calendarData.map((row) => ({
        ...row,
      })),
    [calendarData]
  );

  const CalendarEventItem = (props: SchedulerItemProps) => {
    const { title, description, start, end } = props;
    const tooltip = `${formatTime(start)} - ${formatTime(end)} - ${description ?? ""}`;

    return (
      <SchedulerItem {...props}>
        <div className="k-event-template" title={tooltip}>
          {title}
        </div>
      </SchedulerItem>
    );
  };

  const CustomHeader = ({ schedulerDate }: { schedulerDate: Date | undefined }) => (
    <div
      style={{
        padding: "12px 16px",
        fontSize: "18px",
        fontWeight: 600,
        textAlign: 'center',
        backgroundColor: '#f1f3f5',
        borderBottom: "1px solid #21252921"
      }}
    >
      {schedulerDate?.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })}
    </div>
  );

  return (
    <ContentLayout>
      <ContentHeader>
        <ContentHeaderBackNavigation />
        <ContentHeaderTitle title="My Calendar" />
        <ContentHeaderBreadcrumbs />
        <SearchFormLayout
          withAdvancedFilters
          buttonProps={{
            search: { type: 'submit', onClick: handleSearch },
            clear: { type: 'button', onClick: handleClear },
          }}
        >
          <FormFieldSet cols={2}>
            <ControlledComboBox
              control={control}
              name="monthId"
              label="Month"
              data={MONTHS_OPTIONS}
              dataItemKey="id"
              textField="name"
              wrapperProps={{ className: 'basic-search-field' }}
            />
            <ControlledComboBox
              control={control}
              name="year"
              label="Year"
              data={YEARS_OPTIONS}
              dataItemKey="id"
              textField="name"
              wrapperProps={{ className: 'basic-search-field' }}
            />
            {
              coordinatorOptions?.length ?
              (
                <ControlledComboBox
                  control={control}
                  name="coordinatorId"
                  label="Coordinator"
                  data={coordinatorOptions}
                  dataItemKey="id"
                  textField="fieldName"
                  wrapperProps={{ className: "basic-search-field" }}
                />
              ) : (
                <ControlledInput
                  control={control}
                  name="coordinatorName"
                  label="Coordinator Name"
                  placeholder="Search by coordinator name"
                  wrapperProps={{ className: "basic-search-field" }}
                  readOnly
                />
              )
            }
          </FormFieldSet>
        </SearchFormLayout>
      </ContentHeader>
      <FormLayout>
        <FormSection title="Results">
          <Scheduler
            data={gridData}
            date={calendarMonth}
            header={() => (
              <CustomHeader schedulerDate={calendarMonth} />
            )}
            height={1257}>
              <MonthView
                selectedDateFormat="{0:M}"
                selectedShortDateFormat="{0:M}"
                itemsPerSlot={10}
                item={CalendarEventItem}
              ></MonthView>
          </Scheduler>
        </FormSection>
        <FormActionsBar>
          <Button
            type="button"
            fillMode="outline"
            svgIcon={cancelIcon}
            themeColor="secondary"
            onClick={() => back()}
          >
            Back
          </Button>
        </FormActionsBar>
      </FormLayout>
    </ContentLayout>
  );
};

export default MyCalendar;
